import { useCallback, useEffect, useRef } from 'react';

import {
  clearSaltclickPendingChatEnd,
  getSaltclickPendingChatEnd,
  setSaltclickPendingChatEnd,
} from '~/domain/SaltclickChat';
import { DevLogger } from '~/utils/DevLogger';

/**
 * How long an ended chat is left resumable before its conversation is retired.
 *
 * An end — the user's End chat, a bot timing the session out, an agent closing from their
 * side — ends the messaging *session* and leaves the conversation open. Typing into it
 * starts a new session on the same conversation with its history intact, and that is the
 * window this holds open: reply inside it and nothing is taken away; walk away and the
 * conversation is closed so the next chat starts clean.
 *
 * The cost, agreed with support (2026-09-17): a session resumed this way carries no
 * `gtAdminPanelId`. Salesforce takes hidden pre-chat fields only before a conversation
 * begins, and a resumed session begins none — its own docs say pre-chat data "isn't
 * carried over" to the new session, and the only remedy they offer is on the Omni-Channel
 * flow side. Support would rather keep the history and monitor how often the office is
 * missing.
 */
export const REPLY_WINDOW_MS = 2 * 60 * 1000;

interface IUseSaltclickChatReplyWindowArgs {
  /** Whose window this is. The stored end is kept per account. */
  accountId: string | undefined;
  /** Whether the widget is on screen — the only state a reply can be typed in. */
  chatOnScreen: boolean;
  /** Whether a retire can run yet. The stored end is read back only once it can. */
  canRetire: boolean;
  /** The window closed with nobody replying. `false` keeps the end for the next load. */
  onExpired: (trigger: string, conversationIds: string[]) => Promise<boolean>;
}

interface IPendingEnd {
  trigger: string;
  /** What the widget was holding when it ended; empty when the net could not say. */
  conversationIds: string[];
  /** When the window closes, or `null` while the chat is back on screen. */
  deadline: number | null;
  timer?: number;
}

/**
 * The two minutes an ended chat is left resumable (`REPLY_WINDOW_MS`).
 *
 * Deadline first, timer second: the window is a wall-clock deadline, checked on the timer,
 * on the tab becoming visible again, and on the next page load — whichever notices first
 * retires. It is stored as well, because a timer dies with the document: a tab closed
 * inside the window used to leave the conversation open on Salesforce with nothing left
 * that could ever close it. Mirrors mobile's `useSaltclickChatReplyWindow`.
 *
 * Unlike mobile, several documents can hold the same account's end at once — one per tab —
 * so the stored record is the authority when a timer fires, not the copy in memory.
 */
export const useSaltclickChatReplyWindow = ({
  accountId,
  chatOnScreen,
  canRetire,
  onExpired,
}: IUseSaltclickChatReplyWindowArgs) => {
  const pendingRef = useRef<IPendingEnd | undefined>(undefined);
  // Read through refs so the callbacks below stay stable.
  const onExpiredRef = useRef(onExpired);
  const accountIdRef = useRef(accountId);
  const chatOnScreenRef = useRef(chatOnScreen);
  useEffect(() => {
    onExpiredRef.current = onExpired;
    accountIdRef.current = accountId;
    chatOnScreenRef.current = chatOnScreen;
  }, [accountId, chatOnScreen, onExpired]);
  /** The account whose stored end this document has already read back. */
  const claimedRef = useRef<string | undefined>(undefined);

  const store = useCallback((id: string | undefined, pending?: IPendingEnd) => {
    if (!id) return;

    if (pending) {
      setSaltclickPendingChatEnd(id, {
        conversationIds: pending.conversationIds,
        deadline: pending.deadline,
      });
    } else {
      clearSaltclickPendingChatEnd(id);
    }
  }, []);

  const drop = useCallback(() => {
    window.clearTimeout(pendingRef.current?.timer);
    pendingRef.current = undefined;
  }, []);

  // The timers below are set before `expireIfDue` exists on a later render, so they call
  // through a ref rather than closing over one render's copy.
  const expireIfDueRef = useRef<(noticedBy: string) => Promise<void>>(async () => {});

  const arm = useCallback(
    (pending: IPendingEnd, runFor: number) => {
      window.clearTimeout(pending.timer);
      pending.deadline = Date.now() + runFor;
      pending.timer = window.setTimeout(() => void expireIfDueRef.current('timer'), runFor);
      pendingRef.current = pending;
      claimedRef.current = accountIdRef.current;
      store(accountIdRef.current, pending);
    },
    [store],
  );

  const expireIfDue = useCallback(
    async (noticedBy: string): Promise<void> => {
      const pending = pendingRef.current;
      const id = accountIdRef.current;
      // A suspended end waits on the chat leaving the screen, not on the clock.
      if (!pending || pending.deadline === null || !id) return;

      // Another tab may have suspended, re-armed or settled this end since it was armed.
      const stored = getSaltclickPendingChatEnd(id);
      if (!stored || stored.deadline === null) {
        drop();
        DevLogger.log(`[Saltclick] [${pending.trigger}] the end is now held by another tab`);

        return;
      }

      const remaining = stored.deadline - Date.now();
      if (remaining > 0) {
        // Woken early — a tab coming back with time still on the clock, or another tab
        // re-armed it. Re-arm for what is left rather than trusting a sleeping timer.
        window.clearTimeout(pending.timer);
        pending.deadline = stored.deadline;
        pending.timer = window.setTimeout(() => void expireIfDueRef.current('timer'), remaining);

        return;
      }

      drop();
      DevLogger.log(
        `[Saltclick] [${pending.trigger}] no reply within ${REPLY_WINDOW_MS}ms ` +
          `(noticed by ${noticedBy}) — retiring`,
      );

      const settled = await onExpiredRef.current(
        `${pending.trigger}, unanswered for ${REPLY_WINDOW_MS}ms`,
        stored.conversationIds,
      );

      // Kept when the retire could not finish, so the next load tries again — but not over
      // an end armed while it ran, which owns the record now.
      if (settled && !pendingRef.current) store(id, undefined);
    },
    [drop, store],
  );
  useEffect(() => {
    expireIfDueRef.current = expireIfDue;
  }, [expireIfDue]);

  /** An end arrived. Starts the window, unless one is already running. */
  const armReplyWindow = useCallback(
    (trigger: string, conversationId: string | undefined): void => {
      const pending = pendingRef.current;
      if (pending && pending.deadline !== null) {
        DevLogger.log(`[Saltclick] [${trigger}] an end is already waiting out the reply window`);

        return;
      }

      DevLogger.log(
        `[Saltclick] [${trigger}] chat ended — retiring conversation ${conversationId ?? '?'} ` +
          `in ${REPLY_WINDOW_MS}ms unless the user replies`,
      );
      arm(
        { trigger, conversationIds: conversationId ? [conversationId] : [], deadline: null },
        REPLY_WINDOW_MS,
      );
    },
    [arm],
  );

  /**
   * A session went live. Suspends the pending retirement when it is on the conversation
   * that ended — or when either side could not name one.
   *
   * Only while the widget is on screen: it resumes any open conversation as it boots, and
   * it boots at login on every route, so a status arriving off Help is the warm-up talking
   * and taking it for a reply would cancel every end stored by an earlier load. Suspended
   * rather than dropped, because leaving the chat starts the window again.
   */
  const disarmReplyWindow = useCallback(
    (reason: string, conversationId?: string): void => {
      const pending = pendingRef.current;
      if (!pending || !chatOnScreenRef.current) return;
      if (
        conversationId &&
        pending.conversationIds.length &&
        !pending.conversationIds.includes(conversationId)
      ) {
        return;
      }

      window.clearTimeout(pending.timer);
      pending.timer = undefined;
      pending.deadline = null;
      claimedRef.current = accountIdRef.current;
      store(accountIdRef.current, pending);
      DevLogger.log(
        `[Saltclick] Keeping conversation ${pending.conversationIds.join(', ') || '?'} — ${reason}`,
      );
    },
    [store],
  );

  // Reads back what an earlier page load left pending, unless an end has arrived first —
  // that one is the fresher answer. Once per account, found or not: a retire that keeps
  // failing must wait for the next load, not spin on this effect.
  useEffect(() => {
    if (!accountId || !canRetire || claimedRef.current === accountId) return;
    claimedRef.current = accountId;

    const stored = getSaltclickPendingChatEnd(accountId);
    if (!stored) return;

    DevLogger.log('[Saltclick] An ended conversation was still pending from a previous load');
    // A window suspended when the page went away starts again: whatever the user was doing
    // in that chat, they are not in it now.
    const remaining = stored.deadline === null ? REPLY_WINDOW_MS : stored.deadline - Date.now();
    arm(
      { trigger: 'a previous page load', conversationIds: stored.conversationIds, deadline: null },
      Math.max(remaining, 0),
    );
    if (remaining <= 0) void expireIfDueRef.current('page load');
  }, [accountId, arm, canRetire]);

  /*
   * The window running for the previous account stops here. The stored record
   * deliberately does not: it is keyed by account, so nobody else can read it, and a user
   * who logs out inside the window still has a conversation nothing else will close.
   */
  const seenAccountIdRef = useRef(accountId);
  useEffect(() => {
    if (seenAccountIdRef.current === accountId) return;
    seenAccountIdRef.current = accountId;

    drop();
  }, [accountId, drop]);

  // Leaving the chat starts a suspended window again.
  const seenChatOnScreenRef = useRef(chatOnScreen);
  useEffect(() => {
    const wasOnScreen = seenChatOnScreenRef.current;
    seenChatOnScreenRef.current = chatOnScreen;
    if (chatOnScreen || !wasOnScreen) return;

    const pending = pendingRef.current;
    if (pending && pending.deadline === null) arm(pending, REPLY_WINDOW_MS);
  }, [arm, chatOnScreen]);

  // Background tabs throttle timers, so a tab coming back checks the clock itself.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void expireIfDueRef.current('tab visible');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // The timer belongs to this document; the record deliberately does not. The next load
  // reads it back.
  useEffect(() => drop, [drop]);

  return { armReplyWindow, disarmReplyWindow };
};
